/** Application reporting timezone (Afghanistan — no DST). */
export const APP_TIMEZONE = 'Asia/Kabul';
const WEEKDAY_INDEX = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
export function getDatePartsInAppTimezone(date) {
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: APP_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const read = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour') % 24,
        minute: read('minute'),
        second: read('second'),
        millisecond: date.getMilliseconds(),
    };
}
export function utcFromAppTimezoneParts(parts) {
    let utcMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond);
    for (let i = 0; i < 4; i += 1) {
        const got = getDatePartsInAppTimezone(new Date(utcMs));
        const desiredMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond);
        const gotMs = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute, got.second, got.millisecond);
        utcMs += desiredMs - gotMs;
    }
    return new Date(utcMs);
}
export function startOfDayInAppTimezone(parts) {
    return utcFromAppTimezoneParts({
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
    });
}
export function endOfDayInAppTimezone(parts) {
    return utcFromAppTimezoneParts({
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
    });
}
export function addCalendarDaysInAppTimezone(year, month, day, days) {
    const shifted = new Date(Date.UTC(year, month - 1, day));
    shifted.setUTCDate(shifted.getUTCDate() + days);
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
    };
}
export function lastDayOfMonthInAppTimezone(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
export function getWeekdayInAppTimezone(date) {
    const weekday = new Intl.DateTimeFormat('en-US', {
        timeZone: APP_TIMEZONE,
        weekday: 'short',
    }).format(date);
    return WEEKDAY_INDEX[weekday] ?? 0;
}
/**
 * Map a stored UTC instant to a `Date` whose local getters match Kabul wall clock (for PrimeReact Calendar).
 */
export function toPickerDate(instant) {
    if (!instant || Number.isNaN(instant.getTime())) {
        return null;
    }
    const parts = getDatePartsInAppTimezone(instant);
    return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond);
}
/** Map a PrimeReact picker value (Kabul wall clock) back to a UTC instant. */
export function fromPickerDate(picker) {
    if (!picker || Number.isNaN(picker.getTime())) {
        return null;
    }
    return utcFromAppTimezoneParts({
        year: picker.getFullYear(),
        month: picker.getMonth() + 1,
        day: picker.getDate(),
        hour: picker.getHours(),
        minute: picker.getMinutes(),
        second: picker.getSeconds(),
        millisecond: picker.getMilliseconds(),
    });
}
export function parseApiDateTime(value) {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    const normalized = trimmed.replace(' ', 'T');
    const isoDate = new Date(normalized);
    if (!Number.isNaN(isoDate.getTime())) {
        return isoDate;
    }
    const wallClockMatch = trimmed.match(/^(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (wallClockMatch) {
        const [, a, b, c, h = '0', min = '0', sec = '0'] = wallClockMatch;
        const partA = Number(a);
        const partB = Number(b);
        const partC = Number(c);
        let year;
        let month;
        let day;
        if (partA > 999) {
            year = partA;
            month = partB;
            day = partC;
        }
        else if (partC > 999) {
            year = partC;
            if (partA > 12) {
                day = partA;
                month = partB;
            }
            else if (partB > 12) {
                month = partA;
                day = partB;
            }
            else {
                month = partA;
                day = partB;
            }
        }
        else {
            return null;
        }
        return utcFromAppTimezoneParts({
            year,
            month,
            day,
            hour: Number(h),
            minute: Number(min),
            second: Number(sec),
            millisecond: 0,
        });
    }
    return null;
}
function pad2(value) {
    return String(value).padStart(2, '0');
}
function formatWallClockParts(parts, withTime) {
    const datePart = `${parts.year}/${pad2(parts.month)}/${pad2(parts.day)}`;
    if (!withTime) {
        return datePart;
    }
    return `${datePart} ${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`;
}
function formatValueInAppTimezone(value, withTime) {
    if (value == null || value === '') {
        return '';
    }
    const date = value instanceof Date ? value : parseApiDateTime(String(value));
    if (!date || Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value : '';
    }
    return formatWallClockParts(getDatePartsInAppTimezone(date), withTime);
}
export function formatInAppTimezone(value, options) {
    if (value == null || value === '') {
        return '';
    }
    const date = value instanceof Date ? value : parseApiDateTime(String(value));
    if (!date) {
        return typeof value === 'string' ? value : '';
    }
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: APP_TIMEZONE,
        ...options,
    }).format(date);
}
/** DataTable datetime cells — `YYYY/MM/DD HH:mm:ss` in Kabul time. */
export const TABLE_DATETIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';
/** DataTable date-only cells — `YYYY/MM/DD` in Kabul time. */
export const TABLE_DATE_FORMAT = 'YYYY/MM/DD';
export function formatTableDateTime(value, emptyLabel = '—') {
    if (value == null || value === '') {
        return emptyLabel;
    }
    const formatted = formatValueInAppTimezone(value, true);
    return formatted || emptyLabel;
}
export function formatTableDate(value, emptyLabel = '—') {
    if (value == null || value === '') {
        return emptyLabel;
    }
    const formatted = formatValueInAppTimezone(value, false);
    return formatted || emptyLabel;
}
/** @deprecated Use {@link formatTableDateTime} — kept for existing imports. */
export function formatAppTimezoneDateTime(raw) {
    return formatTableDateTime(raw);
}
